import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import DocumentTreeBrowser from "./DocumentTreeBrowser";
import type { PickedDocument } from "./DocumentTreeBrowser";

interface DocumentTreePickerModalProps {
  companyId: string;
  /** Called with the chosen document right before the modal closes. */
  onPick: (document: PickedDocument) => void;
}

/**
 * Modalx dialog for choosing one existing document out of the whole company
 * documents tree the user can see. Open it with setModalxOptions (title),
 * setModalxComponent("DocumentTreePickerModal") and setModalxComponentProps
 * — in that order, because setModalxComponent clears the props.
 */
export default function DocumentTreePickerModal({ companyId, onPick }: DocumentTreePickerModalProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [selected, setSelected] = useState<PickedDocument | null>(null);

  const close = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const pick = () => {
    if (!selected) return;
    onPick(selected);
    close();
  };

  return (
    <div className="space-y-4">
      <DocumentTreeBrowser
        companyId={companyId}
        rootCategoryIds={[]}
        rootsTitle={t("document_tree_all_folders")}
        excludeFileIds={[]}
        selected={selected}
        onSelect={setSelected}
        onClear={() => setSelected(null)}
      />
      <div className="flex justify-end gap-3 border-t border-zinc-200 pt-4">
        <button
          type="button"
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
          onClick={close}
        >
          {t("cancel")}
        </button>
        <button
          type="button"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          onClick={pick}
          disabled={!selected}
        >
          {t("document_tree_pick")}
        </button>
      </div>
    </div>
  );
}
